import { SelectItem, SelectOption, SelectOptionSource } from '@siemens/element-ng/select';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subject,
  switchMap
} from 'rxjs';

import { ScheduleService } from '../../../schedule/schedule.service';
import { Station } from './map-planner.model';

/**
 * Dynamic station select source for si-select component.
 * Each instance maintains its own search state for independent autocomplete behavior.
 * This follows the pattern from DynamicStationSelectSource in schedule.component.ts
 *
 * Note: This is NOT an injectable service - it's instantiated manually with ScheduleService passed in.
 * Uses a shared cache to ensure station data consistency across multiple select instances.
 */
export class StationSelectSource implements SelectOptionSource<string> {
  /** Subject to handle search input changes and debounce the search requests */
  private searchTermSubject = new Subject<string>();
  private debouncedSearch$: Observable<SelectOption<string>[]>;

  constructor(
    private readonly scheduleService: ScheduleService,
    private readonly sharedCache: Map<string, Station>,
    private readonly excludeStationId?: () => string | undefined
  ) {
    // Initialize the debounced search observable
    this.debouncedSearch$ = this.searchTermSubject.pipe(
      debounceTime(500), // milliseconds
      distinctUntilChanged(), // ensures that the search term is only processed if it has changed
      switchMap(searchTerm => this.fetchOptions(searchTerm))
    );
  }

  /**
   * Get the full station data by ID from the cache
   */
  getStationById(stationId: string): Station | undefined {
    return this.sharedCache.get(stationId);
  }

  /**
   * Add a station to the cache
   * Useful when swapping stations to ensure the station data is available
   */
  addStationToCache(station: Station): void {
    this.sharedCache.set(station.id.toString(), station);
  }

  /**
   * Clear the station cache
   */
  clearCache(): void {
    this.sharedCache.clear();
  }

  /**
   * Cleanup method - completes observables
   * Called when the source is no longer needed
   * Note: Does not clear the shared cache as it may be used by other sources
   */
  cleanup(): void {
    this.searchTermSubject.complete();
  }

  getOptionsForSearch(search: string): Observable<SelectItem<string>[]> {
    // This function is required if filtering is enabled.
    // It is never called with an empty search value.
    return new Observable<SelectItem<string>[]>(observer => {
      const subscription = this.debouncedSearch$.subscribe({
        next: options => {
          observer.next(options);
          observer.complete();
        },
        error: err => observer.error(err)
      });

      this.searchTermSubject.next(search);

      // unsubscribe function to clean up properly
      return () => subscription.unsubscribe();
    });
  }

  getOptionsForValues(values: string[]): Observable<SelectOption<string>[]> {
    // si-select will call this function when it needs to get options for the provided values.
    // This function is typically called when the value is set via a formControl or ngModel.
    if (!values?.length) return of([]);
    const cachedStations = values
      .map(value => this.sharedCache.get(value))
      .filter((station): station is Station => !!station);
    if (cachedStations.length === values.length) {
      return of(this.stationsToSelectOptions(cachedStations));
    }
    return this.scheduleService.getFirstTenEntriesFromStationDB('').pipe(
      map((stations: Station[]) =>
        stations.filter((station: Station) => values.includes(station.id.toString()))
      ),
      map(matches => this.stationsToSelectOptions(matches)),
      catchError(error => {
        console.error('Error fetching station options for values:', error);
        return of([]);
      })
    );
  }

  private fetchOptions(search: string): Observable<SelectOption<string>[]> {
    if (!search) return of([]);
    return this.scheduleService.getFirstTenEntriesFromStationDB(search).pipe(
      map((stations: Station[]) => {
        // Cache the stations for later retrieval in the shared cache
        stations.forEach(station => {
          this.sharedCache.set(station.id.toString(), station);
        });
        // Filter out excluded station if provided
        const excludedId = this.excludeStationId?.();
        const filteredStations = excludedId
          ? stations.filter(s => s.id.toString() !== excludedId)
          : stations;
        return this.stationsToSelectOptions(filteredStations);
      }),
      catchError(error => {
        console.error('Error fetching stations:', error);
        return of([]);
      })
    );
  }

  private stationsToSelectOptions(stations: Station[]): SelectOption<string>[] {
    return stations.map(station => ({
      value: station.id.toString(),
      label: station.stationName,
      type: 'option'
    }));
  }
}
